import { createClient } from '@supabase/supabase-js';
import config from '../config';

const supabaseUrl = config.supabaseUrl;
const supabaseAnonKey = config.supabaseAnonKey;
const supabaseServiceKey = process.env.SUPABASE_SERVICE_KEY;

// Create Supabase client with anon key for regular operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Create Supabase admin client with service key for admin operations
// This will only be available on the server side (in Netlify functions)
export const supabaseAdmin = supabaseServiceKey 
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

/**
 * Sign up with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {string} fullName - User's full name
 * @returns {Promise<Object>} - User data
 */
export const signUp = async (email, password, fullName) => {
  try {
    // Generate a unique verification token
    const verificationToken = Date.now().toString(36) + Math.random().toString(36).substring(2);
    
    // First, sign up with Supabase but disable automatic email verification
    // We need to use the regular signUp method but configure it to not send emails
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          verification_token: verificationToken, // Store the token in user metadata
        },
        emailRedirectTo: null // Setting to null should prevent the email from being sent
      },
    });

    if (error) throw error;

    // Create user profile
    if (data?.user) {
      try {
        await supabase.from('user_profiles').insert({
          id: data.user.id,
          email: data.user.email,
          full_name: fullName,
          email_verified: false, // Explicitly set to false until verified
        });
      } catch (profileError) {
        console.error('Error creating user profile:', profileError);
        // Continue even if profile creation fails
      }

      // Send our custom verification email
      try {
        // Create a verification link with our custom token
        const verificationLink = `${config.baseUrl}/verify?token=${verificationToken}&email=${encodeURIComponent(email)}`;
        
        console.log('Using custom verification link:', verificationLink);
        
        // Log the base URL to ensure it's correct
        console.log('Base URL from config:', config.baseUrl);
        
        // Send verification email using our Netlify Function
        const response = await fetch('/.netlify/functions/send-verification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            verificationLink,
            fullName,
          }),
        });
        
        const result = await response.json();
        if (!result.success) {
          console.error('Error sending verification email:', result.error);
        } else {
          console.log('Verification email sent successfully');
        }
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // Continue even if email sending fails
      }
    }

    return data;
  } catch (error) {
    console.error('Error in signUp:', error);
    throw error;
  }
};

// Note: Email verification is now handled by the Netlify function

/**
 * Sign in with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} - Session data
 */
export const signIn = async (email, password) => {
  // First, try to sign in
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    // If the error is about email not confirmed, provide a clearer message
    if (error.message.includes('Email not confirmed')) {
      throw new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
    }
    throw error;
  }

  // Check if the user's email is confirmed in auth.users
  if (data?.user && !data.user.email_confirmed_at) {
    throw new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
  }

  // Also check if the email is verified in user_profiles
  try {
    const { data: profileData, error: profileError } = await supabase
      .from('user_profiles')
      .select('email_verified')
      .eq('id', data.user.id)
      .single();

    if (profileError) {
      console.error('Error checking email verification status:', profileError);
      // Instead of continuing, throw an error to prevent login
      throw new Error('Unable to verify email status. Please try again or contact support.');
    } 
    
    if (!profileData || profileData.email_verified !== true) {
      // If email_verified is false or missing in user_profiles, throw an error
      throw new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
    }
  } catch (err) {
    console.error('Error in email verification check:', err);
    // Rethrow the error to prevent login
    throw new Error(err.message || 'Error verifying email status. Please try again later.');
  }

  return data;
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

/**
 * Get the current user
 * @returns {Promise<Object|null>} - User data or null if not authenticated
 */
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data?.user || null;
};

/**
 * Get the current session
 * @returns {Promise<Object|null>} - Session data or null if not authenticated
 */
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data?.session || null;
};

/**
 * Get user profile
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User profile data
 */
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} - Updated profile data
 */
export const updateUserProfile = async (userId, profileData) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...profileData,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Reset password
 * @param {string} email - User's email
 * @returns {Promise<boolean>} - True if successful
 */
export const resetPassword = async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${config.baseUrl}/reset-password`,
  });

  if (error) throw error;
  return true;
};

/**
 * Update password
 * @param {string} newPassword - New password
 * @returns {Promise<boolean>} - True if successful
 */
export const updatePassword = async (newPassword) => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) throw error;
  return true;
};

/**
 * Set up auth state change listener
 * @param {Function} callback - Callback function to handle auth state changes
 * @returns {Function} - Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  // Type-safe wrapper for the callback
  const typedCallback = (event, session) => {
    return callback(event, session);
  };
  
  const { data } = supabase.auth.onAuthStateChange(typedCallback);
  return data.subscription.unsubscribe;
};

export default {
  supabase,
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  getSession,
  getUserProfile,
  updateUserProfile,
  resetPassword,
  updatePassword,
  onAuthStateChange,
};
